import React, { useEffect, useState, useRef } from 'react';
import shaka from 'shaka-player/dist/shaka-player.ui';
import 'shaka-player/dist/controls.css';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import BackButton from './BackButton';
import { BASE_URL } from '../../utils/globals';
import { getMovieById } from '../../api/movies';
import { getPedagogicalVideosById } from '../../api/pedagogical-videos';
import { PlayerLanguages } from '../../utils/player';
import { handleLogout } from '../../utils/utils';

import ErrorIcon from '@mui/icons-material/Error';
import './Player.css';

export default function Player() {
    const [manifestUri, setManifestUri] = useState('');
    const [subtitles, setSubtitles] = useState([]);
    const [error, setError] = useState({ isError: false, message: '' });

    const { id } = useParams();
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const uiRef = useRef(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pedagogical = searchParams.get('pedagogical') === 'true';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = pedagogical ? await getPedagogicalVideosById(id) : await getMovieById(id);

                setManifestUri(res.ManifestUrl || []);
                setSubtitles(res.Subtitles || []);
            } catch (error) {
                const statusCode = error?.statusCode;
                if (statusCode === 403) {
                    setError({ isError: true, message: t('Player.PleaseSignIn') });
                    handleLogout();
                } else {
                    setError({ isError: true, message: t('ErrorOccured.Title') });
                }
                console.error(error);
            }
        };

        fetchData();
    }, [id, pedagogical, t]);

    useEffect(() => {
        let player;
        let ui;

        const initPlayer = async () => {
            shaka.polyfill.installAll();

            if (!shaka.Player.isBrowserSupported()) {
                console.error('Shaka Player is not supported on this browser.');
                return;
            }

            const video = videoRef.current;
            const container = containerRef.current;
            if (!video || !container) return;

            player = new shaka.Player(video);
            ui = new shaka.ui.Overlay(player, container, video);

            playerRef.current = player;
            uiRef.current = ui;

            const controls = ui.getControls();
            const localization = controls.getLocalization();
            const currentLanguage = i18next.language.toLowerCase();
            const playerLocale = PlayerLanguages[currentLanguage] || 'en';
            localization.changeLocale([playerLocale]);

            // Helpful extra logging
            player.addEventListener('error', (evt) => {
                console.error('Shaka error event:', evt.detail);
            });

            try {
                await player.load(manifestUri);

                // Add subtitles if we have them
                const uniqueSubtitles = Array.from(new Map(subtitles.map((s) => [s.language, s])).values());

                for (const s of uniqueSubtitles) {
                    await player.addTextTrackAsync(`${BASE_URL}${s.file.url}`, s.language, 'subtitles');
                }
            } catch (err) {
                console.error('Error loading video:', err);
            }
        };

        initPlayer();

        return () => {
            if (ui) ui.destroy();
            if (player) player.destroy();
            playerRef.current = null;
            uiRef.current = null;
        };
    }, [manifestUri, subtitles]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && ['s', 'u'].includes(e.key.toLowerCase())) {
                e.preventDefault();
            }
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    if (error.isError) {
        return (
            <div className="shaka-video-container error">
                <ErrorIcon sx={{ fontSize: 300 }} htmlColor="#e74c3c" />
                <h2 className="player-error-message">{error.message}</h2>
                <BackButton pedagogical={pedagogical} id={id} />
            </div>
        );
    }

    return (
        <div ref={containerRef} className="shaka-video-container" onContextMenu={(e) => e.preventDefault()}>
            <video ref={videoRef} className="shaka-video" type="video/mp4" autoPlay />

            <BackButton pedagogical={pedagogical} id={id} />
        </div>
    );
}
